import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSearch, faUserSecret } from '@fortawesome/free-solid-svg-icons';
import { impersonateModalAtom } from '../../../atoms/Atoms';
import { backdropVariants, modalVariants } from '../../../utils';
import bookingApi from '../../../api/bookingApi';
import useImpersonation from '../../../hooks/useImpersonation';

// SuperAdmin-only modal: free-text search of the user directory, then
// impersonate the selected user. Driven by impersonateModalAtom.
function ImpersonateModal() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useRecoilState(impersonateModalAtom);
  const { startImpersonation } = useImpersonation();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [submittingId, setSubmittingId] = useState(null);

  const close = () => {
    setIsOpen(false);
  };

  // Reset transient state whenever the modal is opened.
  useEffect(() => {
    if (isOpen) {
      setTerm('');
      setResults([]);
      setSearching(false);
      setSubmittingId(null);
    }
  }, [isOpen]);

  // Debounced search against the admin directory endpoint.
  useEffect(() => {
    if (!isOpen) return undefined;
    const value = term.trim();
    if (value.length < 2) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    const handle = setTimeout(() => {
      bookingApi
        .post('admin/users/search', { searchTerm: value })
        .then((response) => {
          setResults(response.data.data || []);
        })
        .catch(() => {
          setResults([]);
        })
        .finally(() => {
          setSearching(false);
        });
    }, 400);
    return () => clearTimeout(handle);
  }, [term, isOpen]);

  const handleSelect = async (selected) => {
    setSubmittingId(selected.id);
    try {
      await startImpersonation(selected.id);
    } finally {
      setSubmittingId(null);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-24 bg-black bg-opacity-60"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={close}
        >
          <motion.div
            className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 bg-yellow-500 text-white">
              <div className="flex items-center space-x-2 font-bold tracking-tight">
                <FontAwesomeIcon icon={faUserSecret} />
                <span>{t('impersonate_user')}</span>
              </div>
              <button
                type="button"
                onClick={close}
                className="outline-none focus:outline-none hover:scale-90 transition duration-300"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="p-5">
              <div className="relative">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  autoFocus
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder={t('impersonate_search_placeholder')}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm text-black outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                />
              </div>

              <div className="mt-4 max-h-80 overflow-y-auto divide-y divide-gray-100">
                {searching && (
                  <div className="py-6 text-center text-sm text-gray-400">
                    {t('loading')}
                  </div>
                )}
                {!searching && term.trim().length >= 2 && results.length === 0 && (
                  <div className="py-6 text-center text-sm text-gray-400">
                    {t('impersonate_no_results')}
                  </div>
                )}
                {!searching && term.trim().length < 2 && (
                  <div className="py-6 text-center text-sm text-gray-400">
                    {t('impersonate_search_hint')}
                  </div>
                )}
                {results.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    disabled={!!submittingId}
                    onClick={() => handleSelect(u)}
                    className="w-full flex items-center justify-between text-left px-2 py-3 hover:bg-gray-50 transition duration-300 disabled:opacity-50"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-black truncate">
                        {u.completeName || `${u.firstName || ''} ${u.lastName || ''}`.trim()}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{u.email}</div>
                      {u.roles && u.roles.length > 0 && (
                        <div className="text-[10px] uppercase tracking-wide text-yellow-600 truncate">
                          {u.roles.join(', ')}
                        </div>
                      )}
                    </div>
                    <span className="ml-3 flex-shrink-0 text-xs font-semibold text-yellow-600">
                      {submittingId === u.id ? t('loading') : t('impersonate')}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default ImpersonateModal;
